/**
 * The change strip (handoff §15.2): what the last edit did to the gaps, in one line,
 * with «Вернуть» when it can be undone. On a phone it is pinned above the keyboard (the
 * page puts it in the frame's footer); on desktop it sits under the editor.
 */
import { Info } from 'lucide-react'
import { useLocale } from '../../../i18n/context'
import type { Strip } from '../../../utils/textEdit'
import { GhostButton } from '../ui'

function stripText(strip: Strip, te: ReturnType<typeof useLocale>['tr']['textEdit']): string {
  switch (strip.kind) {
    case 'removed': return te.stripRemoved(strip.form)
    case 'phrase': return te.stripPhrase(strip.form)
    case 'grew': return te.stripGrew(strip.form, strip.count)
    case 'empty': return te.stripEmpty
  }
}

export default function ChangeStrip({ strip, onUndo, bar = false }: {
  strip: Strip
  onUndo: () => void
  /** The pinned phone bar: 56 px of chrome with a top hairline. Otherwise a plain row. */
  bar?: boolean
}) {
  const { tr } = useLocale()
  const te = tr.textEdit
  return (
    <div
      role="status"
      data-testid="edit-change-strip"
      style={{
        minHeight: 56, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 10,
        padding: '0 4px 0 16px', fontFamily: 'var(--font-ui)',
        ...(bar
          ? { background: 'var(--bg-chrome)', borderTop: '1px solid var(--border-chrome)' }
          : { borderRadius: 12, background: 'var(--accent-soft)', padding: '0 4px 0 14px' }),
      }}
    >
      <Info size={18} aria-hidden style={{ flexShrink: 0, color: 'var(--accent)' }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.35, color: 'var(--text-primary)' }}>
        {stripText(strip, te)}
      </span>
      {strip.kind !== 'empty' && strip.kind !== 'grew' && (
        <GhostButton onClick={onUndo} data-testid="edit-strip-undo" style={{ flexShrink: 0, fontSize: 13 }}>
          {te.stripUndo}
        </GhostButton>
      )}
    </div>
  )
}
